'use client';

// =============================================================================
// usePullToRefresh — простая реализация pull-to-refresh для TG WebApp.
// Native browser pull-to-refresh заблокирован через `overscroll-behavior: none`
// в globals.css (мы намеренно отключили его, чтобы не было конфликта с TG
// WebApp swipe-to-close), поэтому делаем свою через touch-события.
//
// Использование:
//   const { pullDistance, isRefreshing } = usePullToRefresh(refresh, threshold);
//   <PullIndicator distance={pullDistance} active={isRefreshing} />
//
// Логика:
//   • touchstart at scrollY==0 → начинаем tracking
//   • touchmove → копим dy (positive = pull down), показываем indicator
//   • touchend → если dy >= threshold → trigger onRefresh, показываем spinner
//   • после onRefresh resolves → reset state
// =============================================================================

import { useEffect, useRef, useState } from 'react';

interface PullState {
  pullDistance: number;
  isRefreshing: boolean;
}

const DEFAULT_THRESHOLD = 80;
const MAX_PULL = 140; // capped, чтобы indicator не уезжал слишком далеко

export function usePullToRefresh(
  onRefresh: () => void | Promise<void>,
  threshold: number = DEFAULT_THRESHOLD,
): PullState {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const tracking = useRef(false);

  useEffect(() => {
    function handleTouchStart(e: TouchEvent) {
      // Запускаем pull-to-refresh только когда юзер на самом верху страницы.
      // window.scrollY === 0 на iOS бывает -1 при rubber-band, проверяем <= 0.
      if (window.scrollY > 0) return;
      if (isRefreshing) return;
      const t = e.touches[0];
      if (!t) return;
      startY.current = t.clientY;
      tracking.current = true;
    }

    function handleTouchMove(e: TouchEvent) {
      if (!tracking.current || startY.current === null) return;
      const t = e.touches[0];
      if (!t) return;
      const dy = t.clientY - startY.current;
      if (dy <= 0) {
        // Юзер тянет ВВЕРХ → выходим из pull-режима, обычный scroll.
        setPullDistance(0);
        tracking.current = false;
        return;
      }
      // Damping: чем дальше тянем, тем медленнее реагируем (ощущение упругости).
      const damped = Math.min(MAX_PULL, dy * 0.5);
      setPullDistance(damped);
    }

    function handleTouchEnd() {
      if (!tracking.current) return;
      tracking.current = false;
      const reached = pullDistance >= threshold;
      if (reached && !isRefreshing) {
        setIsRefreshing(true);
        Promise.resolve(onRefresh())
          .catch(() => {
            /* swallow — onRefresh должен сам показать ошибку */
          })
          .finally(() => {
            setIsRefreshing(false);
            setPullDistance(0);
          });
      } else {
        setPullDistance(0);
      }
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: true });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [onRefresh, threshold, pullDistance, isRefreshing]);

  return { pullDistance, isRefreshing };
}
